import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Shield, Plus, Warning, Trash, Flask, Sparkle, Info, CheckCircle, ArrowsClockwise } from '@phosphor-icons/react';
import { API_URL } from '../config/api';
import { SEO } from '../components/SEO';

const CATEGORIES = [
  { value: 'fungicide', label: 'Fungicide (FRAC)' },
  { value: 'herbicide', label: 'Herbicide (HRAC)' },
  { value: 'insecticide', label: 'Insecticide (IRAC)' },
  { value: 'pgr', label: 'PGR' },
];

const CATEGORY_LABELS = {
  fungicide: 'Fungicide',
  herbicide: 'Herbicide',
  insecticide: 'Insecticide',
  pgr: 'PGR',
};

const ResistancePage = () => {
  const navigate = useNavigate();
  const [actives, setActives] = useState([]);
  const [history, setHistory] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [form, setForm] = useState({
    product_name: '',
    active_ingredient: '',
    category: 'fungicide',
    zone_name: '',
    rate_note: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    try {
      const [actRes, histRes] = await Promise.all([
        axios.get(`${API_URL}/api/resistance/actives`),
        axios.get(`${API_URL}/api/resistance/history`, { withCredentials: true }),
      ]);
      setActives(actRes.data.actives || []);
      setHistory(histRes.data);
    } catch {
      toast.error('Failed to load rotation history');
    }

    // Load suggestions for each category in parallel
    const sug = {};
    for (const cat of ['fungicide', 'herbicide', 'insecticide']) {
      try {
        const r = await axios.get(`${API_URL}/api/resistance/suggest-next?category=${cat}`, { withCredentials: true });
        sug[cat] = r.data;
      } catch {
        /* ignore */
      }
    }
    setSuggestions(sug);
  };

  useEffect(() => { loadAll(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.product_name.trim() || !form.active_ingredient.trim()) {
      toast.error('Product name and active ingredient are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/resistance/log`, form, { withCredentials: true });
      toast.success('Application logged');
      setForm({ ...form, product_name: '', active_ingredient: '', rate_note: '', notes: '', zone_name: '' });
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Log failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await axios.delete(`${API_URL}/api/resistance/log/${id}`, { withCredentials: true });
      await loadAll();
      toast.success('Deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const activesForCategory = actives.filter(a => {
    if (form.category === 'fungicide') return a.system === 'FRAC';
    if (form.category === 'herbicide') return a.system === 'HRAC';
    if (form.category === 'insecticide') return a.system === 'IRAC';
    if (form.category === 'pgr') return a.system === 'PGR';
    return false;
  });

  const selectedActive = actives.find(a => a.name === form.active_ingredient);

  return (
    <div className="min-h-screen bg-background" data-testid="resistance-page">
      <SEO title="Resistance Rotation — TurfPulse" description="Track your fungicide, herbicide, and insecticide applications by FRAC / HRAC / IRAC group to avoid resistance build-up." />

      <header className="border-b border-border/60 bg-card/80 backdrop-blur-sm sticky top-0 z-30">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-4 py-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/app')} data-testid="res-back-btn">
            <ArrowLeft size={16} className="mr-1.5" /> Dashboard
          </Button>
          <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
            <Sparkle weight="fill" size={12} /> Pro-grade
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
            <Shield size={12} weight="fill" /> FRAC / HRAC / IRAC
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Resistance Rotation</h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Every time you spray, you either prevent or build resistance. TurfPulse tags each product by its mode-of-action group and warns you when you're stacking the same MoA — the same way golf-course agronomists plan their programs.
          </p>
        </div>

        {/* Warnings */}
        {history?.warnings?.length > 0 && (
          <div className="space-y-2" data-testid="res-warnings">
            {history.warnings.map((w, i) => (
              <div
                key={i}
                className={`flex items-start gap-2 rounded-lg border p-3 text-sm ${
                  w.severity === 'high'
                    ? 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400'
                    : 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400'
                }`}
                data-testid={`res-warn-${i}`}
              >
                <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                <div className="flex-1">
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-0.5">{w.severity} · {CATEGORY_LABELS[w.category]}</div>
                  <div>{w.message}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {history?.insights?.length > 0 && (
          <div className="space-y-2" data-testid="res-insights">
            {history.insights.map((ins, i) => (
              <div key={i} className="flex items-start gap-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 p-3 text-sm">
                <CheckCircle size={16} weight="fill" className="shrink-0 mt-0.5" />
                <span>{ins.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Log form */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Plus weight="bold" size={16} className="text-primary" />
              <h2 className="text-base font-semibold">Log an application</h2>
            </div>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="res-form">
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v, active_ingredient: '' })}>
                  <SelectTrigger className="mt-1" data-testid="res-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Product name *</Label>
                <Input
                  value={form.product_name}
                  onChange={(e) => setForm({ ...form, product_name: e.target.value })}
                  placeholder="e.g., Scotts DiseaseEx"
                  className="mt-1"
                  data-testid="res-product"
                />
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Active ingredient *</Label>
                <Select value={form.active_ingredient} onValueChange={(v) => setForm({ ...form, active_ingredient: v })}>
                  <SelectTrigger className="mt-1" data-testid="res-active">
                    <SelectValue placeholder="Pick from 46 known actives" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    {activesForCategory.map(a => (
                      <SelectItem key={a.name} value={a.name}>
                        {a.name} — {a.system} {a.group}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedActive && (
                  <p className="text-[11px] text-muted-foreground mt-1">
                    <span className="font-semibold">{selectedActive.system} Group {selectedActive.group}</span> · {selectedActive.moa}
                  </p>
                )}
              </div>
              <div>
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Zone / area (optional)</Label>
                <Input
                  value={form.zone_name}
                  onChange={(e) => setForm({ ...form, zone_name: e.target.value })}
                  placeholder="Front yard"
                  className="mt-1"
                  data-testid="res-zone"
                />
              </div>
              <div className="md:col-span-2">
                <Label className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rate / notes (optional)</Label>
                <Input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g., 2 lbs per 1,000 sqft, watered in"
                  className="mt-1"
                  data-testid="res-notes"
                />
              </div>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={saving} data-testid="res-submit">
                  {saving ? 'Saving…' : 'Log application'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Suggested-next rotation panels */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="res-suggestions">
          {['fungicide', 'herbicide', 'insecticide'].map(cat => {
            const s = suggestions[cat];
            if (!s) return null;
            return (
              <Card key={cat}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <ArrowsClockwise size={12} />
                    Rotate {CATEGORY_LABELS[cat]}
                  </div>
                  <div className="text-xs">
                    Last group: <span className="font-mono font-semibold">{s.last_group_applied || 'none'}</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {s.suggested_next_groups.length === 0 ? (
                      <span className="text-xs text-muted-foreground">No recent apps — any group is safe.</span>
                    ) : (
                      s.suggested_next_groups.slice(0, 6).map(g => (
                        <span key={g.group} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary border border-primary/20" title={g.moa}>
                          {g.system} {g.group}
                        </span>
                      ))
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* History table */}
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Flask weight="bold" size={16} className="text-primary" />
              <h2 className="text-base font-semibold">Application history</h2>
              {history && <span className="text-[11px] text-muted-foreground">({history.count} entries)</span>}
            </div>
            <div className="rounded-lg border border-border/60 overflow-hidden" data-testid="res-history">
              <div className="grid grid-cols-[1fr_1.5fr_1fr_1fr_auto] gap-2 px-3 py-2 bg-muted/40 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                <div>Date</div>
                <div>Product</div>
                <div>Active</div>
                <div>Group</div>
                <div></div>
              </div>
              {!history?.entries?.length ? (
                <div className="p-6 text-center text-sm text-muted-foreground" data-testid="res-empty">
                  No applications logged yet.
                </div>
              ) : (
                history.entries.map(e => (
                  <div key={e.id} className="grid grid-cols-[1fr_1.5fr_1fr_1fr_auto] gap-2 px-3 py-2 border-t border-border/40 text-sm items-center" data-testid={`res-row-${e.id}`}>
                    <div className="text-xs text-muted-foreground">{e.date}</div>
                    <div className="truncate">
                      <div className="font-medium truncate">{e.product_name}</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider">{e.category}{e.zone_name ? ` · ${e.zone_name}` : ''}</div>
                    </div>
                    <div className="text-xs">{e.active_ingredient}</div>
                    <div>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted font-mono" title={e.moa}>
                        {e.system} {e.group}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                      onClick={() => handleDelete(e.id)}
                      data-testid={`res-delete-${e.id}`}
                    >
                      <Trash size={13} />
                    </Button>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex items-start gap-2 rounded-md bg-primary/5 border border-primary/20 p-3 text-xs">
          <Info size={13} className="shrink-0 mt-0.5 text-primary" />
          <span>
            <strong>How rotation works:</strong> Every active ingredient is sorted into a numbered group by its mode of action.
            Apply the same group repeatedly and resistant weed/disease/insect populations dominate. Best practice: rotate between
            at least 2 groups per category per season; for QoI fungicides (FRAC 11), cap at 2 apps per season and always pair/follow with another MoA.
          </span>
        </div>
      </main>
    </div>
  );
};

export default ResistancePage;
